import {
  ReactElement,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { Navigate, useLocation, useOutlet } from "react-router";
import { twMerge } from "tailwind-merge";
import type { RouteHref } from "../../constants/routes";
import { useWindowSize } from "../../hooks";
import { DashboardContext } from "./Dashboard.shared";
import { TwoPanelLayout } from "./TwoPanelLayout";

export function Dashboard({
  width,
  parentComponent,
  parentPart = "1fr",
  childPart = "1fr",
  defaultChild,
}: {
  width: number;
  parentComponent: ReactNode;
  parentPart?: string;
  childPart?: string;
  defaultChild?: RouteHref;
}): ReactElement {
  const { width: windowWidth } = useWindowSize();
  const location = useLocation();
  const outlet = useOutlet();
  const parentPartRef = useRef<HTMLDivElement>(null);
  const singlePartRef = useRef<HTMLDivElement>(null);
  const isListRoute = !outlet;
  const isWindowLargerThanWidth = (windowWidth ?? 0) >= width;
  const getParentScrollPosition = useCallback(
    () => parentPartRef.current?.scrollTop ?? 0,
    [],
  );
  const setParentScrollPosition = useCallback((scrollTop: number) => {
    parentPartRef.current?.scrollTo({ top: scrollTop });
  }, []);
  const dashboardContext = useMemo(
    () => ({
      isTwoPanel: isWindowLargerThanWidth,
      getParentScrollPosition,
      setParentScrollPosition,
      singlePartRef,
    }),
    [getParentScrollPosition, isWindowLargerThanWidth, setParentScrollPosition],
  );

  useLayoutEffect(() => {
    singlePartRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  return (
    <DashboardContext.Provider value={dashboardContext}>
      {isWindowLargerThanWidth && isListRoute && defaultChild ? (
        <Navigate to={defaultChild} replace />
      ) : null}
      {isWindowLargerThanWidth ? (
        <TwoPanelLayout
          first={parentComponent}
          second={isListRoute ? null : outlet}
          firstPart={parentPart}
          secondPart={childPart}
          firstRef={parentPartRef}
          secondRef={singlePartRef}
          firstClassName="border-r border-solid border-gray-300 bg-white print:hidden"
        />
      ) : (
        <div className="grid max-w-[100vw] sm:min-h-full print:block print:w-screen">
          <div
            className={twMerge(
              "max-w-full print:hidden",
              !isListRoute && "hidden",
            )}
          >
            {parentComponent}
          </div>
          {isListRoute ? null : (
            <div className="max-w-[100vw]" ref={singlePartRef}>
              {outlet}
            </div>
          )}
        </div>
      )}
    </DashboardContext.Provider>
  );
}
